import React, { useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  compose,
  displayMoney,
  IN_APP_PRODUCT_TYPE,
  resolveDocumentUrl,
} from '../../services/utilityFunctions';
import AppProductProp from '../profile/propTypes/appProductProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import StripeCardProp from '../profile/propTypes/StripeCardProp';
import PrintModal from '../profile/mainPanel/print/PrintModal';
import EnrollModal from '../academy/EnrollModal';
import Loader from '../../services/loader';
import MetaContext from '../../contextProviders/MetaContext';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import Notifications from '../../services/notifications';
import ShareModal from '../academy/modal/ShareModal';
import CertificateProp from '../academy/propTypes/certificateProp';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import IsAcademyPublic from '../../commonComponents/IsAcademyPublic';
import Request from '../../services/request';

function CertificatePreview(props) {
  const {
    t,
    _csrf,
    certificate,
    appProducts,
    countries,
    cards,
    onCardsChange,
    hasAcademyHeader,
  } = props;
  const loader = useRef(new Loader('#certificateLayoutContainer'));

  const { branding } = useContext(MetaContext);
  const { loggedUser } = useContext(LoggedUserContext);

  const [isFetching, setFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCertificate, setSavedCertificate] = useState(certificate);
  const [columnSize, setColumnSize] = useState(12);

  const [modalInfoShare, setModalInfoShare] = useState({ show: false });
  /**
   * BRAVON-6332 Phase 2b, Stage 6 — publish the PUBLIC copy of the certificate
   * image before the share link leaves this page. The image itself is private
   * and reaches the browser as a presigned url that expires in an hour, which
   * the OG scrapers behind a shared link can do nothing with.
   *
   * Idempotent, and deliberately fire-and-forget: the link works either way —
   * only its preview card would be missing — so a failure must not stop the
   * modal from opening.
   */
  const publishShareImage = id => {
    if (!id) return;
    new Request(_csrf).post(`/certificate/${id}/share`).fail(err => {
      Notifications.showErrorFromRequest(err, 'error_fetchCertificate');
    });
  };

  const showModalShare = () => {
    publishShareImage(savedCertificate && savedCertificate._id);
    setModalInfoShare({ show: true });
  };
  const hideModalShare = () => setModalInfoShare({ show: false });

  const [modalInfoPrint, setModalInfoPrint] = useState({ show: false });
  const showModalPrint = () => setModalInfoPrint({ show: true });
  const hideModalPrint = () => setModalInfoPrint({ show: false });

  const [modalInfoBuy, setModalInfoBuy] = useState({ show: false });
  const showModalBuy = () => setModalInfoBuy({ show: true });
  const hideModalBuy = () => setModalInfoBuy({ show: false });

  const appProductPrint = appProducts.find(
    p => p.productType === IN_APP_PRODUCT_TYPE.CERTIFICATE,
  );
  const hasImage = savedCertificate.image && savedCertificate.image.url;

  const certificateImage =
    savedCertificate && savedCertificate.image
      ? savedCertificate.image.url
      : '';

  const appName = branding && branding.appName ? branding.appName : 'Bravon';

  useEffect(() => {
    if (loader.current) {
      if (isFetching) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isFetching]);

  const onReload = () => {
    setIsLoading(true);
    new Request(_csrf)
      .post(`/certificate/${certificate._id}/preview`)
      .done(({ data }) => {
        setSavedCertificate(data);
        setIsLoading(false);
      })
      .fail(err => {
        Notifications.showErrorFromRequest(err, 'error_fetchCertificate');
        setIsLoading(false);
      });
  };

  const priceForProduct = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return displayMoney(
      product.userCurrency.total,
      product.userCurrency.symbol,
    );
  };

  const isProductFree = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return product.userCurrency.total === 0;
  };

  const productCurrency = type => {
    const product = appProducts.find(p => p.productType === type);
    if (!product || !product.userCurrency) {
      return null;
    }
    return product.userCurrency.code;
  };

  const { protocol, host } =
    typeof window !== 'undefined' ? window.location : {};
  const fallBackShareUrl = `${protocol}//${host}/certificate/${savedCertificate.code}`;

  const requestFreeCertificate = () => {
    setFetching(true);
    const params = {
      owner: loggedUser._id,
      amount: 0,
      currency: productCurrency(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE),
    };

    new Request(_csrf)
      .post(`/certificate/${certificate._id}/email`, params)
      .done(({ data }) => {
        setFetching(false);
        Notifications.showNotificationSuccess(
          t('success'),
          t('certificate_purchased'),
        );
        window.open(resolveDocumentUrl(data.link), '_blank');
      })
      .fail(err => {
        setFetching(false);
        Notifications.showErrorFromRequest(err, 'error_certificatePurchase');
      });
  };

  useEffect(() => {
    let numberOfButtons = priceForProduct(
      IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE,
    )
      ? 2
      : 1;
    if (hasAcademyHeader) {
      numberOfButtons += 1;
    }
    setColumnSize(12 / numberOfButtons);
  }, []);

  return (
    <div id="certificateLayoutContainer" className="certificate-display">
      {modalInfoShare.show && (
        <ShareModal
          url={certificate.share || fallBackShareUrl}
          onClose={hideModalShare}
        />
      )}
      {modalInfoBuy.show && (
        <EnrollModal
          itemType={IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE}
          itemId={certificate._id}
          onClose={hideModalBuy}
        />
      )}
      {modalInfoPrint.show && (
        <PrintModal
          certificateId={certificate ? certificate._id : null}
          cards={cards}
          countries={countries}
          appProduct={appProductPrint}
          onClose={hideModalPrint}
          onCardsChange={onCardsChange}
        />
      )}
      {hasImage && !certificate.isLocked ? (
        <div className="print-preview-content">
          <div className="watermark">
            {[1, 2, 3, 4].map(i => (
              <p key={i}>
                {[1, 2, 3, 4].map(() => `${appName.toUpperCase()}    `)}
              </p>
            ))}
          </div>
          <div className="certificate-preview-wrapper">
            <>
              <img
                alt="certificate preview"
                className="certificate-preview"
                src={certificateImage}
              />
            </>
          </div>
        </div>
      ) : (
        <div className="certificate-body">
          <div className="certificate-img">
            <div className="certificate-no-preview">
              {t('certificate_noPreview')}
            </div>
            <div className="certificate-placeholder">
              <img
                className="certificate-placeholder"
                alt="certificate preview"
                src="/images/academy/certificate/certificate.png"
              />
              {!certificate.isLocked && (
                <button
                  type="button"
                  className={`certificate-overlay-button ${
                    isLoading ? 'loading' : ''
                  }`}
                  style={{ top: '35%' }}
                  onClick={onReload}
                >
                  <img
                    alt="refresh arrow"
                    src="/images/academy/certificate/load.svg"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {!certificate.isLocked && (
        <div className="certificate-bottom">
          <div className="row">
            {priceForProduct(IN_APP_PRODUCT_TYPE.CERTIFICATE) && (
              <div className={`col-lg-${columnSize}`}>
                <h5>
                  {t(
                    'certificate_byLetter',
                    priceForProduct(IN_APP_PRODUCT_TYPE.CERTIFICATE),
                  )}
                </h5>
                <button
                  type="button"
                  onClick={showModalPrint}
                  className="btn m-btn--pill m-btn--wide btn-dynamic print-button"
                >
                  {t('certificate_orderNow')}
                </button>
              </div>
            )}
            {(priceForProduct(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE) ||
              isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)) && (
              <div className={`col-lg-${columnSize}`}>
                <h5>
                  {isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                    ? t('certificate_byEmail_free')
                    : t(
                        'certificate_byEmail',
                        priceForProduct(
                          IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE,
                        ),
                      )}
                </h5>
                <button
                  type="button"
                  className="btn m-btn--pill m-btn--wide btn-dynamic print-button"
                  onClick={
                    isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                      ? requestFreeCertificate
                      : showModalBuy
                  }
                >
                  {t(
                    isProductFree(IN_APP_PRODUCT_TYPE.DIGITAL_CERTIFICATE)
                      ? 'certificate_download'
                      : 'certificate_orderNow',
                  )}
                </button>
              </div>
            )}
            {hasAcademyHeader && (
              <div className={`col-lg-${columnSize}`}>
                <h5>{t('certificate_shareOnSocial')}</h5>
                <button
                  onClick={showModalShare}
                  type="button"
                  className="btn m-btn--pill m-btn--wide btn-outline-dynamic print-button"
                >
                  {t('certificate_share')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

CertificatePreview.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  certificate: PropTypes.shape(CertificateProp.propType),
  appProducts: PropTypes.arrayOf(PropTypes.shape(AppProductProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType)).isRequired,
  onCardsChange: PropTypes.func,
  hasAcademyHeader: PropTypes.bool.isRequired,
};

CertificatePreview.defaultProps = {
  certificate: null,
  onCardsChange: null,
};

export default compose(
  translate,
  withCSRF,
  IsAcademyPublic,
)(CertificatePreview);
